/**
 * True probability + CI with optional base-line axis (Models tab / Competitors tab).
 */
import React, { useRef } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from 'recharts';

const FAIR_PROB_LINE_COLOR = '#B45309';

export default function TrueProbCiChart({
  data,
  baselineShiftPoints = [],
  currentBaseLine,
  fixtureStartTimeMs,
  showStartLine = false,
  formatAxisTime,
  makeChartTooltip,
  chartTooltipWrapperProps,
  modelFusePoints = [],
  emptyMessage = 'No history. Click Refresh after updates.',
  height = 320,
}) {
  const chartRef = useRef(null);

  if (!data?.length) {
    return <div className="empty-state compact">{emptyMessage}</div>;
  }

  return (
    <div ref={chartRef} className="chart-tooltip-anchor">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(96,165,250,0.2)" />
          <XAxis
            dataKey="time"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            stroke="#94A3B8"
            tickFormatter={formatAxisTime}
            tick={{ fontSize: 11 }}
          />
          <YAxis yAxisId="left" stroke="#94A3B8" domain={[0, 1]} />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#F59E0B"
            domain={['dataMin - 0.5', 'dataMax + 0.5']}
            tickFormatter={(v) => v.toFixed(1)}
            label={{ value: 'Line', angle: 90, position: 'insideRight', fill: '#F59E0B', fontSize: 11 }}
          />
          <Tooltip
            {...chartTooltipWrapperProps}
            content={makeChartTooltip(
              (value, name) => {
                if (name === 'Base Line') return [value?.toFixed(1), name];
                if (name === 'Model fuse') return [`${(value * 100).toFixed(2)}%`, name];
                return [`${(value * 100).toFixed(2)}%`, name];
              },
              { chartAnchorRef: chartRef },
            )}
          />
          <Legend />
          {showStartLine && fixtureStartTimeMs != null && (
            <ReferenceLine
              x={fixtureStartTimeMs}
              stroke="#22C55E"
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{ value: 'Start', fill: '#22C55E', fontSize: 11, position: 'top' }}
              ifOverflow="extendDomain"
              yAxisId="left"
            />
          )}
          {modelFusePoints.map((pt, idx) => (
            <ReferenceDot
              key={pt.time ?? idx}
              x={pt.time}
              y={pt.p_fair}
              yAxisId="left"
              r={5}
              fill="#A78BFA"
              stroke="#E9D5FF"
              strokeWidth={1}
              ifOverflow="extendDomain"
            />
          ))}
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="p_fair"
            stroke={FAIR_PROB_LINE_COLOR}
            strokeWidth={2}
            name="P(Fair)"
            dot={false}
            connectNulls
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="p_lower"
            stroke="#64748B"
            strokeWidth={1}
            strokeDasharray="5 5"
            name="Lower CI"
            dot={false}
            connectNulls
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="p_upper"
            stroke="#64748B"
            strokeWidth={1}
            strokeDasharray="5 5"
            name="Upper CI"
            dot={false}
            connectNulls
          />
          <Line
            yAxisId="right"
            type="stepAfter"
            dataKey="base_line"
            stroke="#F59E0B"
            strokeWidth={2}
            name="Base Line"
            dot={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
      {currentBaseLine != null && (
        <p className="risk-card-lede risk-card-lede--compact" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
          Published line {Number(currentBaseLine).toFixed(1)} · purple dots = model fuse outputs
        </p>
      )}
    </div>
  );
}
